// static/table_engine_src/core/ops_clipboard.js
import {
  parseDelimited,
  gridToHtmlTable,
  htmlTableToGrid,
  clipboardReadData,
  clipboardReadText,
  clipboardWriteData,
  clipboardWriteText,
} from "./clipboard.js";
import { dateParseOptionsForCol, isDateCol, normalizeDateValue } from "../util/date.js";

/**
 * Installs (on ctx):
 *   ctx.copyToClipboard()
 *   ctx.pasteFromClipboard()
 *   ctx.fillDownFromSelection()
 *   ctx.copyRowsToTSV(rowIdxs, { includeOutputs? })
 *   ctx.pasteIntoRows(rowStartIdx)
 *   ctx.firstPasteableInputColIndex()
 *
 * Expects ctx to already have:
 *   - ctx.spec, ctx.cols, ctx.colByKey, ctx.hiddenCols
 *   - ctx.getRowEls(), ctx.getInput(tr,k), ctx.getOut(tr,k)
 *   - ctx.getSelection(), ctx.getPasteStart()
 *   - ctx.getAnyRawDataset(el), ctx.clearRawDatasets(el)
 *   - ctx.isNumericCol(col), ctx.colScale(col)
 *   - ctx.setNumericInputValue(el,col,v)
 *   - ctx.updateTotals(), ctx.scheduleAutoWidth?.(), ctx.scheduleNotifyRendered?.()
 *   - ctx.notify (optional)
 *   - ctx.onEdit (optional)
 *
 * OPTION A SUPPORT:
 *   - If ctx._teSuppressCopyNotify === true, copyToClipboard MUST NOT toast.
 *     (cmdCopy in commands.js becomes the single source of truth for "Copied ...")
 */
export function installClipboardOps(ctx) {
  if (!ctx) return;
  const parseNum = (value, fmt = null) =>
    (typeof ctx.parseNumber === "function" ? ctx.parseNumber(value, NaN, fmt) : Number(value));

  const fallbackNotify = {
    ok: (m) => console.log(m),
    err: (m) => console.warn(m),
    info: (m) => console.log(m),
  };
  const getNotify = () => ctx.notify || window.notify || fallbackNotify;

  function shouldCopyNotify() {
    return !ctx._teSuppressCopyNotify;
  }

  // ✅ IMPORTANT: make clipboard helpers available to other modules (menu_builders, commands, etc.)
  // The (TSV) menu items call ctx.clipboardWriteText; without this they always fail.
  if (typeof ctx.clipboardWriteText !== "function") ctx.clipboardWriteText = clipboardWriteText;
  if (typeof ctx.clipboardReadText !== "function") ctx.clipboardReadText = clipboardReadText;
  if (typeof ctx.clipboardWriteData !== "function") ctx.clipboardWriteData = clipboardWriteData;
  if (typeof ctx.clipboardReadData !== "function") ctx.clipboardReadData = clipboardReadData;
  if (typeof ctx.parseDelimited !== "function") ctx.parseDelimited = parseDelimited;

  function isLockedCol(col) {
    return !!col?.locked;
  }

  function canPasteIntoCol(col) {
    if (!col) return false;
    if (ctx.hiddenCols?.has?.(col.key)) return false;
    if (col.kind !== "input") return false;
    if (col.readonly) return false;
    if (isLockedCol(col)) return false;
    return true;
  }

  function cellTextForCopy(tr, col) {
    if (!tr || !col) return "";
    if (ctx.hiddenCols?.has?.(col.key)) return "";
    if (col.kind === "rowHandle") return "";
    if (col.kind === "actions") return "";

    if (col.kind === "input") {
      const el = ctx.getInput?.(tr, col.key);
      return el ? String(el.value ?? "") : "";
    }
    if (col.kind === "output") {
      const el = ctx.getOut?.(tr, col.key);
      return el ? String(el.textContent ?? "") : "";
    }
    return "";
  }

  function visibleKeys({ includeOutputs = false } = {}) {
    const cols = ctx.cols || [];
    return cols
      .filter((c) => {
        if (!c?.key) return false;
        if (ctx.hiddenCols?.has?.(c.key)) return false;
        if (c.kind === "rowHandle") return false;
        if (c.kind === "actions") return false;
        if (!includeOutputs && c.kind === "output") return false;
        return c.kind === "input" || c.kind === "output";
      })
      .map((c) => c.key);
  }

  function copyRowsToTSV(rowIdxs, { includeOutputs = false } = {}) {
    const rows = ctx.getRowEls?.() || [];
    const keys = visibleKeys({ includeOutputs });
    const out = [];

    (rowIdxs || []).forEach((r) => {
      const tr = rows[r];
      if (!tr) return;
      const line = keys.map((k) => {
        const col = ctx.colByKey?.get?.(k);
        return cellTextForCopy(tr, col);
      });
      out.push(line.join("\t"));
    });

    return out.join("\n");
  }

  function copySelectionToTSV() {
    const cols = ctx.cols || [];
    const rows = ctx.getRowEls?.() || [];
    const colByKey = ctx.colByKey;
    const hiddenCols = ctx.hiddenCols;

    const s = ctx._sel || {};

    const rangeToTsv = (range) => {
      if (!range) return "";
      const { r0, r1, c0, c1 } = range;
      const out = [];
      for (let r = r0; r <= r1; r++) {
        const tr = rows[r];
        const line = [];
        for (let c = c0; c <= c1; c++) {
          const col = cols[c];
          if (!col || hiddenCols?.has?.(col.key) || col.kind === "rowHandle" || col.kind === "actions") {
            line.push("");
            continue;
          }
          line.push(cellTextForCopy(tr, col));
        }
        out.push(line.join("\t"));
      }
      return out.join("\n");
    };

    // 1) Cell range
    if (s.cellRange || (Array.isArray(s.cellRanges) && s.cellRanges.length)) {
      const seen = new Set();
      const ranges = [...(Array.isArray(s.cellRanges) ? s.cellRanges : []), ...(s.cellRange ? [s.cellRange] : [])]
        .filter((rg) => rg && Number.isInteger(rg.r0) && Number.isInteger(rg.r1) && Number.isInteger(rg.c0) && Number.isInteger(rg.c1))
        .filter((rg) => {
          const k = `${rg.r0}:${rg.r1}:${rg.c0}:${rg.c1}`;
          if (seen.has(k)) return false;
          seen.add(k);
          return true;
        });
      if (!ranges.length) return "";
      return ranges.map((rg) => rangeToTsv(rg)).filter(Boolean).join("\n\n");
    }

    // 2) Column selection
    if (s.selectedCols?.size) {
      const keys = cols
        .map((c) => c?.key)
        .filter(Boolean)
        .filter((k) => s.selectedCols.has(k))
        .filter((k) => !hiddenCols?.has?.(k))
        .filter((k) => {
          const col = colByKey?.get?.(k);
          if (!col) return false;
          if (col.kind === "rowHandle" || col.kind === "actions") return false;
          return col.kind === "input" || col.kind === "output";
        });

      if (!keys.length) return "";

      const out = [];
      rows.forEach((tr) => {
        const line = keys.map((k) => {
          const col = colByKey?.get?.(k);
          return cellTextForCopy(tr, col);
        });
        out.push(line.join("\t"));
      });
      return out.join("\n");
    }

    // 3) Row selection
    if (s.selectedRows?.size) {
      const idxs = [...s.selectedRows]
        .filter((x) => Number.isInteger(x))
        .sort((a, b) => a - b);

      if (!idxs.length) return "";
      return copyRowsToTSV(idxs, { includeOutputs: false });
    }

    // 4) Single cell anchor
    if (s.cellAnchor) {
      const tr = rows[s.cellAnchor.r];
      const col = cols[s.cellAnchor.c];
      if (!tr || !col) return "";
      if (hiddenCols?.has?.(col.key) || col.kind === "rowHandle" || col.kind === "actions") return "";
      return cellTextForCopy(tr, col);
    }

    return "";
  }

  function copySelectionToHtml() {
    const tsv = copySelectionToTSV();
    if (!tsv) return "";
    const grid = parseDelimited(tsv);
    if (!grid.length) return "";
    return gridToHtmlTable(grid);
  }

  function firstPasteableInputColIndex() {
    const cols = ctx.cols || [];
    for (let c = 0; c < cols.length; c++) {
      if (canPasteIntoCol(cols[c])) return c;
    }
    return -1;
  }

  function finalizeBulkWrite({ notifyMsg = "", touchedRows = null, touchedCols = null } = {}) {
    if (notifyMsg) getNotify()?.ok?.(notifyMsg);

    const rows =
      touchedRows instanceof Set
        ? touchedRows
        : new Set(Array.isArray(touchedRows) ? touchedRows : []);
    if (!rows.size) return;

    const cols =
      touchedCols instanceof Set
        ? touchedCols
        : new Set(Array.isArray(touchedCols) ? touchedCols : []);

    ctx.onEdit?.();
    ctx.markTotalsDirtyRows?.([...(rows || [])]);
    ctx.updateTotals?.();

    const keys = [...cols].filter(Boolean);
    if (keys.length) ctx.scheduleAutoWidth?.(keys);
    else ctx.scheduleAutoWidth?.();

    ctx.scheduleNotifyRendered?.();
  }

  function finalizePasteWrite({
    notifyMsg = "",
    touchedRows = null,
    touchedCols = null,
    anchor = null,
  } = {}) {
    finalizeBulkWrite({ notifyMsg, touchedRows, touchedCols });

    const a =
      anchor &&
      Number.isInteger(anchor.r) &&
      Number.isInteger(anchor.c)
        ? { r: anchor.r, c: anchor.c }
        : null;

    try {
      ctx.clearCopySourceVisual?.({ repaint: false });
    } catch {}

    if (a && typeof ctx.setCellAnchor === "function") {
      ctx.setCellAnchor(a.r, a.c);
      return;
    }

    ctx.applySelectionVisuals?.();
  }

  function applyPastedValueToCell(tr, rowIdx, col, value, touched) {
    if (!col || col.kind !== "input") {
      touched.skipped += 1;
      return;
    }
    if (!canPasteIntoCol(col)) {
      touched.skipped += 1;
      return;
    }

    const el = ctx.getInput?.(tr, col.key);
    if (!el || el.readOnly || el.disabled) {
      touched.skipped += 1;
      return;
    }

    const v = value;
    const emitSyntheticInputOnPaste = ctx.emitSyntheticInputOnPaste !== false;
    const emitInputEvent = () => {
      if (!emitSyntheticInputOnPaste) return;
      try {
        el.dispatchEvent(new Event("input", { bubbles: true }));
      } catch {}
    };

    const isNumericCol =
      (typeof ctx.isNumericCol === "function" && !!ctx.isNumericCol(col)) ||
      col.type === "number" ||
      col.type === "currency" ||
      col.type === "percent";

    if (isNumericCol) {
      const s = String(v ?? "").trim();
      if (s === "") {
        ctx.setNumericInputValue?.(el, col, "");
        emitInputEvent();
        touched.rows.add(rowIdx);
        touched.cols.add(col.key);
        touched.applied += 1;
        return;
      }
      const fmt = ctx.effectiveFormatForCol?.(col) || ctx.normalizeFormat?.(col) || null;
      const nDisp = parseNum(s, fmt);
      if (Number.isFinite(nDisp)) {
        const sc = ctx.colScale?.(col) ?? 1;
        ctx.setNumericInputValue?.(el, col, nDisp / sc);
        emitInputEvent();
        touched.rows.add(rowIdx);
        touched.cols.add(col.key);
        touched.applied += 1;
      } else {
        ctx.setNumericInputValue?.(el, col, s);
        emitInputEvent();
        touched.rows.add(rowIdx);
        touched.cols.add(col.key);
        touched.applied += 1;
      }
      return;
    }

    if (isDateCol(col)) {
      const s = String(v ?? "").trim();
      if (s === "") {
        el.value = "";
      } else {
        const iso = normalizeDateValue(s, dateParseOptionsForCol(col));
        if (iso) el.value = iso;
        else el.value = String(el.type || "").toLowerCase() === "date" ? "" : s;
      }
      emitInputEvent();
      touched.rows.add(rowIdx);
      touched.cols.add(col.key);
      touched.applied += 1;
      return;
    }

    el.value = v;
    emitInputEvent();
    touched.rows.add(rowIdx);
    touched.cols.add(col.key);
    touched.applied += 1;
  }

  function pasteGridAt(startR, startC, grid) {
    const touched = { rows: new Set(), cols: new Set(), applied: 0, skipped: 0 };
    if (!grid || !grid.length) return touched;

    const rows = ctx.getRowEls?.() || [];
    const cols = ctx.cols || [];

    for (let r = 0; r < grid.length; r++) {
      const tr = rows[startR + r];
      if (!tr) break;

      const rowVals = grid[r];
      for (let c = 0; c < rowVals.length; c++) {
        const col = cols[startC + c];
        applyPastedValueToCell(tr, startR + r, col, rowVals[c], touched);
      }
    }

    return touched;
  }

  function normalizeRange(range) {
    if (!range) return null;
    const { r0, r1, c0, c1 } = range;
    if (![r0, r1, c0, c1].every(Number.isInteger)) return null;
    return {
      r0: Math.min(r0, r1),
      r1: Math.max(r0, r1),
      c0: Math.min(c0, c1),
      c1: Math.max(c0, c1),
    };
  }

  function selectedRanges() {
    if (typeof ctx.getSelections === "function") {
      const ranges = ctx.getSelections();
      if (Array.isArray(ranges) && ranges.length) return ranges;
    }
    const one = ctx.getSelection?.();
    return one ? [one] : [];
  }

  function normalizedSelectionRanges() {
    const ranges = selectedRanges();
    const out = [];
    const seen = new Set();
    for (const range of ranges) {
      const rg = normalizeRange(range);
      if (!rg) continue;
      const key = `${rg.r0}:${rg.r1}:${rg.c0}:${rg.c1}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(rg);
    }
    return out;
  }

  async function runHistoryTransaction(options = {}, worker = null) {
    const fn =
      typeof worker === "function"
        ? worker
        : typeof options === "function"
          ? options
          : null;
    if (typeof fn !== "function") return undefined;

    if (typeof ctx.runHistoryTransaction === "function") {
      return await ctx.runHistoryTransaction(options, fn);
    }
    return await fn();
  }

  function normalizeClipboardPayload(payload) {
    if (typeof payload === "string") {
      return { text: String(payload), html: "" };
    }
    if (!payload || typeof payload !== "object") {
      return { text: "", html: "" };
    }
    return {
      text: String(payload.text ?? ""),
      html: String(payload.html ?? ""),
    };
  }

  function gridFromClipboardPayload(payload) {
    const p = normalizeClipboardPayload(payload);
    if (p.text.trim()) return parseDelimited(p.text);
    if (p.html.trim()) return htmlTableToGrid(p.html);
    return [];
  }

  async function readClipboardPayloadSafe() {
    const readData =
      (typeof ctx.clipboardReadData === "function" ? ctx.clipboardReadData : clipboardReadData);
    const readText =
      (typeof ctx.clipboardReadText === "function" ? ctx.clipboardReadText : clipboardReadText);
    const configuredTimeoutMs = Number(ctx.clipboardReadTimeoutMs);
    const timeoutMs = Number.isFinite(configuredTimeoutMs) && configuredTimeoutMs > 0
      ? configuredTimeoutMs
      : 1200;
    const timeoutToken = {};

    try {
      const payload = await Promise.race([
        Promise.resolve().then(() => readData()),
        new Promise((resolve) => setTimeout(() => resolve(timeoutToken), timeoutMs)),
      ]);
      if (payload === timeoutToken || payload == null) return { text: "", html: "" };
      const normalized = normalizeClipboardPayload(payload);
      if (normalized.text || normalized.html) return normalized;
    } catch {
      // fall through to text-only read
    }

    try {
      const text = await Promise.race([
        Promise.resolve().then(() => readText()),
        new Promise((resolve) => setTimeout(() => resolve(timeoutToken), timeoutMs)),
      ]);
      if (text === timeoutToken || text == null) return { text: "", html: "" };
      return { text: String(text), html: "" };
    } catch {
      return { text: "", html: "" };
    }
  }

  function pasteSingleValueIntoSelection(value, ranges) {
    const touched = { rows: new Set(), cols: new Set(), applied: 0, skipped: 0 };
    if (!Array.isArray(ranges) || !ranges.length) return touched;

    const rows = ctx.getRowEls?.() || [];
    const cols = ctx.cols || [];
    const seen = new Set();

    for (const range of ranges) {
      const rg = normalizeRange(range);
      if (!rg) continue;

      for (let r = rg.r0; r <= rg.r1; r++) {
        const tr = rows[r];
        if (!tr) continue;

        for (let c = rg.c0; c <= rg.c1; c++) {
          const key = `${r}:${c}`;
          if (seen.has(key)) continue;
          seen.add(key);

          const col = cols[c];
          applyPastedValueToCell(tr, r, col, value, touched);
        }
      }
    }

    return touched;
  }

  function rangeFromAnchorAndGrid(anchor, grid) {
    if (!anchor || !Number.isInteger(anchor.r) || !Number.isInteger(anchor.c)) return null;
    const rowsN = Math.max(1, Array.isArray(grid) ? grid.length : 0);
    const colsN = Math.max(
      1,
      ...(Array.isArray(grid) ? grid : []).map((r) => (Array.isArray(r) ? r.length : 0)),
    );
    return normalizeRange({
      r0: anchor.r,
      r1: anchor.r + rowsN - 1,
      c0: anchor.c,
      c1: anchor.c + colsN - 1,
    });
  }

  async function pasteFromClipboard(rawPayload = null) {
    let payload = normalizeClipboardPayload(rawPayload);
    if (!payload.text && !payload.html) payload = await readClipboardPayloadSafe();
    if (!payload.text && !payload.html) {
      const fallbackText = String(ctx._teLastCopiedText || "");
      const fallbackHtml = String(ctx._teLastCopiedHtml || "");
      payload = normalizeClipboardPayload({ text: fallbackText, html: fallbackHtml });
    }
    if (!payload.text && !payload.html) {
      getNotify()?.err?.("Paste failed: clipboard not accessible.");
      return;
    }

    const grid = gridFromClipboardPayload(payload);
    if (!grid.length) return;

    const ranges = normalizedSelectionRanges();
    if (ranges.length) {
      const first = ranges[0];
      const pasteAnchor = first ? { r: first.r0, c: first.c0 } : null;

      const touched = { rows: new Set(), cols: new Set(), applied: 0, skipped: 0 };
      await runHistoryTransaction(
        {
          label: "paste",
          ranges,
          anchor: pasteAnchor,
        },
        async () => {
          const rows = ctx.getRowEls?.() || [];
          const cols = ctx.cols || [];
          const gridRows = grid.length;
          const gridCols = Math.max(
            1,
            ...grid.map((r) => (Array.isArray(r) ? r.length : 0)),
          );

          const seen = new Set();
          for (const rg of ranges) {
            for (let r = rg.r0; r <= rg.r1; r++) {
              const tr = rows[r];
              if (!tr) continue;

              const sr = (r - rg.r0) % gridRows;
              const srcRow = Array.isArray(grid[sr]) ? grid[sr] : [];

              for (let c = rg.c0; c <= rg.c1; c++) {
                const key = `${r}:${c}`;
                if (seen.has(key)) continue;
                seen.add(key);

                const sc = (c - rg.c0) % gridCols;
                const val = sc < srcRow.length ? srcRow[sc] : "";
                const col = cols[c];
                applyPastedValueToCell(tr, r, col, val, touched);
              }
            }
          }
        },
      );

      if (touched.rows.size) {
        const msg =
          touched.skipped > 0
            ? `Pasted ${touched.applied} cell(s), skipped ${touched.skipped} locked/output cell(s).`
            : `Pasted ${touched.applied} cell(s).`;
        finalizePasteWrite({
          notifyMsg: msg,
          touchedRows: touched.rows,
          touchedCols: touched.cols,
          anchor: pasteAnchor,
        });
        return;
      }
      getNotify()?.err?.("No editable cells in selected range.");
      return;
    }

    let start;
    if (ctx._sel?.cellRange) start = { r: ctx._sel.cellRange.r0, c: ctx._sel.cellRange.c0 };
    else start = ctx.getPasteStart?.() || { r: 0, c: 0 };

    let touched = { rows: new Set(), cols: new Set(), applied: 0, skipped: 0 };
    const txRange = rangeFromAnchorAndGrid(start, grid);
    await runHistoryTransaction(
      {
        label: "paste",
        ranges: txRange ? [txRange] : [],
        anchor: start,
      },
      async () => {
        touched = pasteGridAt(start.r, start.c, grid);
      },
    );

    if (!touched.rows.size) {
      getNotify()?.err?.("No editable cells at paste target.");
      return;
    }
    const msg =
      touched.skipped > 0
        ? `Pasted ${touched.applied} cell(s), skipped ${touched.skipped} locked/output cell(s).`
        : `Pasted ${touched.applied} cell(s).`;
    finalizePasteWrite({
      notifyMsg: msg,
      touchedRows: touched.rows,
      touchedCols: touched.cols,
      anchor: start,
    });
  }

  async function copyToClipboard() {
    const tsv = copySelectionToTSV();
    if (!tsv) return false;
    const html = copySelectionToHtml();
    ctx._teLastCopiedText = tsv;
    ctx._teLastCopiedHtml = html || "";

    const write =
      (typeof ctx.clipboardWriteData === "function" ? ctx.clipboardWriteData : clipboardWriteData);
    const ok = await write({ text: tsv, html });

    if (ok) {
      try {
        ctx.captureCopySourceFromSelection?.();
      } catch {}
      if (shouldCopyNotify()) getNotify()?.ok?.("Copied.");
      return true;
    } else {
      getNotify()?.err?.("Copy failed.");
      return false;
    }
  }

  async function pasteIntoRows(rowStartIdx) {
    let payload = await readClipboardPayloadSafe();
    if (!payload.text && !payload.html) {
      payload = normalizeClipboardPayload({
        text: String(ctx._teLastCopiedText || ""),
        html: String(ctx._teLastCopiedHtml || ""),
      });
    }
    if (!payload.text && !payload.html) {
      getNotify()?.err?.("Paste failed: clipboard not accessible.");
      return;
    }
    const grid = gridFromClipboardPayload(payload);
    if (!grid.length) return;

    const startC = firstPasteableInputColIndex();
    if (startC < 0) {
      getNotify()?.err?.("No pasteable input columns.");
      return;
    }

    let touched = { rows: new Set(), cols: new Set(), applied: 0, skipped: 0 };
    const txRange = rangeFromAnchorAndGrid({ r: rowStartIdx, c: startC }, grid);
    await runHistoryTransaction(
      {
        label: "paste-rows",
        ranges: txRange ? [txRange] : [],
        anchor: { r: rowStartIdx, c: startC },
      },
      async () => {
        touched = pasteGridAt(rowStartIdx, startC, grid);
      },
    );

    finalizePasteWrite({
      notifyMsg: "Pasted rows.",
      touchedRows: touched.rows,
      touchedCols: touched.cols,
      anchor: { r: rowStartIdx, c: startC },
    });
  }

  function isNumericFillCol(col) {
    return (
      (typeof ctx.isNumericCol === "function" && !!ctx.isNumericCol(col)) ||
      col?.type === "number" ||
      col?.type === "currency" ||
      col?.type === "percent"
    );
  }

  function parseNumericInputValue(el, col) {
    const raw = String(ctx.getAnyRawDataset?.(el) ?? "").trim();
    if (raw !== "") {
      const nRaw = parseNum(raw);
      if (Number.isFinite(nRaw)) return nRaw;
    }

    const s = String(el?.value ?? "").trim();
    if (s === "") return null;

    const fmt = ctx.effectiveFormatForCol?.(col) || ctx.normalizeFormat?.(col) || null;
    const nDisp = parseNum(s, fmt);
    if (!Number.isFinite(nDisp)) return null;
    const sc = ctx.colScale?.(col) ?? 1;
    return nDisp / sc;
  }

  function dateToSerial(iso) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || ""));
    if (!m) return null;
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = Number(m[3]);
    if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return null;
    return Math.round(Date.UTC(y, mo - 1, d) / 86400000);
  }

  function serialToIso(serial) {
    const n = Number(serial);
    if (!Number.isFinite(n)) return "";
    const dt = new Date(Math.round(n) * 86400000);
    const y = dt.getUTCFullYear();
    const m = String(dt.getUTCMonth() + 1).padStart(2, "0");
    const d = String(dt.getUTCDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  function detectTextNumericSeries(values) {
    if (!Array.isArray(values) || values.length < 2) return null;
    const pats = [];
    for (const value of values) {
      const s = String(value ?? "");
      const m = /^(.*?)(\d+)(\D*)$/.exec(s);
      if (!m) return null;
      pats.push({
        prefix: m[1],
        digits: m[2],
        suffix: m[3],
        value: Number(m[2]),
      });
    }
    if (!pats.every((p) => Number.isFinite(p.value))) return null;

    const prefix = pats[0].prefix;
    const suffix = pats[0].suffix;
    const width = pats[0].digits.length;
    if (!pats.every((p) => p.prefix === prefix && p.suffix === suffix && p.digits.length === width)) {
      return null;
    }

    const step = pats.length >= 2 ? pats[pats.length - 1].value - pats[pats.length - 2].value : 0;
    if (!Number.isFinite(step)) return null;
    return {
      type: "text-series",
      prefix,
      suffix,
      width,
      base: pats[pats.length - 1].value,
      step,
    };
  }

  function buildFillPlan(col, sourceValues) {
    const values = Array.isArray(sourceValues) ? sourceValues : [];
    if (!values.length) return null;

    if (isNumericFillCol(col)) {
      const allFinite = values.every((v) => Number.isFinite(v));
      if (allFinite) {
        const last = Number(values[values.length - 1]);
        if (values.length >= 2) {
          const first = Number(values[0]);
          const step = (last - first) / (values.length - 1);
          return { type: "number-series", base: last, step: Number.isFinite(step) ? step : 0 };
        }
        return { type: "number-constant", value: last };
      }
      return {
        type: "pattern",
        values: values.map((v) => (Number.isFinite(v) ? v : "")),
      };
    }

    if (isDateCol(col)) {
      const serials = values.map((v) => dateToSerial(v));
      const allValid = serials.every((v) => Number.isFinite(v));
      if (allValid) {
        const last = Number(serials[serials.length - 1]);
        if (serials.length >= 2) {
          const first = Number(serials[0]);
          const step = Math.round((last - first) / (serials.length - 1));
          return { type: "date-series", base: last, step: Number.isFinite(step) ? step : 0 };
        }
        return { type: "date-constant", value: serialToIso(last) };
      }
      return {
        type: "pattern",
        values: values.map((v) => String(v ?? "")),
      };
    }

    const textSeries = detectTextNumericSeries(values);
    if (textSeries) return textSeries;
    return {
      type: "pattern",
      values: values.map((v) => String(v ?? "")),
    };
  }

  function valueFromFillPlan(plan, offset) {
    if (!plan) return "";

    const nOffset = Math.max(1, Number(offset) || 1);
    if (plan.type === "number-series") {
      const n = Number(plan.base) + Number(plan.step) * nOffset;
      return Number.isFinite(n) ? Number(n.toPrecision(15)) : "";
    }
    if (plan.type === "number-constant") return Number(plan.value);
    if (plan.type === "date-series") return serialToIso(Number(plan.base) + Number(plan.step) * nOffset);
    if (plan.type === "date-constant") return String(plan.value || "");
    if (plan.type === "text-series") {
      const next = Math.round(Number(plan.base) + Number(plan.step) * nOffset);
      const digits = String(Math.abs(next)).padStart(plan.width, "0");
      const signed = next < 0 ? `-${digits}` : digits;
      return `${plan.prefix}${signed}${plan.suffix}`;
    }
    if (plan.type === "pattern") {
      const vals = Array.isArray(plan.values) && plan.values.length ? plan.values : [""];
      return vals[(nOffset - 1) % vals.length];
    }
    return "";
  }

  function fillDownRange(sel, { targetRow = null } = {}) {
    const range = normalizeRange(sel);
    if (!range) return { rows: new Set(), cols: new Set(), changed: false };

    const { r0, c0, r1, c1 } = range;
    const rows = ctx.getRowEls?.() || [];
    if (!rows.length) return { rows: new Set(), cols: new Set(), changed: false };

    const toRow = Number.isInteger(targetRow)
      ? Math.max(r1, Math.min(rows.length - 1, targetRow))
      : r1;
    const fillWithinSelection = !Number.isInteger(targetRow) || targetRow <= r1;
    const fillStartRow = fillWithinSelection ? r0 + 1 : r1 + 1;
    const fillEndRow = fillWithinSelection ? r1 : toRow;
    if (fillStartRow > fillEndRow) return { rows: new Set(), cols: new Set(), changed: false };

    const sourceStartRow = r0;
    const sourceEndRow = fillWithinSelection ? r0 : r1;

    const cols = ctx.cols || [];
    const plansByCol = new Map();

    for (let c = c0; c <= c1; c++) {
      const col = cols[c];
      if (!col || col.kind !== "input" || !canPasteIntoCol(col)) continue;

      const sourceValues = [];
      for (let r = sourceStartRow; r <= sourceEndRow; r++) {
        const tr = rows[r];
        if (!tr) continue;
        const el = ctx.getInput?.(tr, col.key);
        if (!el) continue;

        if (isNumericFillCol(col)) {
          sourceValues.push(parseNumericInputValue(el, col));
          continue;
        }

        if (isDateCol(col)) {
          const s = String(el.value ?? "").trim();
          if (s === "") sourceValues.push("");
          else {
            const iso = normalizeDateValue(s, dateParseOptionsForCol(col));
            sourceValues.push(iso || s);
          }
          continue;
        }

        sourceValues.push(String(el.value ?? ""));
      }

      const plan = buildFillPlan(col, sourceValues);
      if (plan) plansByCol.set(col.key, plan);
    }

    const touchedRows = new Set();
    const touchedCols = new Set();
    for (let r = fillStartRow; r <= fillEndRow; r++) {
      const tr = rows[r];
      if (!tr) continue;
      const offset = fillWithinSelection ? r - r0 : r - r1;

      for (let c = c0; c <= c1; c++) {
        const col = cols[c];
        if (!col || col.kind !== "input" || !canPasteIntoCol(col)) continue;

        const el = ctx.getInput?.(tr, col.key);
        if (!el || el.readOnly || el.disabled) continue;

        const plan = plansByCol.get(col.key);
        if (!plan) continue;

        const value = valueFromFillPlan(plan, offset);
        if (isNumericFillCol(col)) {
          ctx.setNumericInputValue?.(el, col, value);
        } else if (isDateCol(col)) {
          const s = String(value ?? "").trim();
          if (s === "") el.value = "";
          else {
            const iso = normalizeDateValue(s, dateParseOptionsForCol(col));
            if (iso) el.value = iso;
            else el.value = String(el.type || "").toLowerCase() === "date" ? "" : s;
          }
        } else {
          el.value = String(value ?? "");
        }

        touchedRows.add(r);
        touchedCols.add(col.key);
      }
    }

    return { rows: touchedRows, cols: touchedCols, changed: touchedRows.size > 0 };
  }

  async function fillDownFromSelection() {
    const selections = normalizedSelectionRanges();
    if (!selections.length) return;

    const touchedRows = new Set();
    const touchedCols = new Set();
    let changed = false;

    await runHistoryTransaction(
      {
        label: "fill-down",
        ranges: selections,
        anchor: { r: selections[0].r0, c: selections[0].c0 },
      },
      async () => {
        for (const sel of selections) {
          const res = fillDownRange(sel);
          if (!res?.changed) continue;
          changed = true;
          res.rows.forEach((r) => touchedRows.add(r));
          res.cols.forEach((c) => touchedCols.add(c));
        }
      },
    );

    if (!changed) return;
    finalizeBulkWrite({
      notifyMsg: "Filled down.",
      touchedRows,
      touchedCols,
    });
  }

  async function fillDownSelectionToRow(targetRow) {
    const sel = normalizeRange(ctx.getSelection?.());
    if (!sel) return;
    if (!Number.isInteger(targetRow)) return;

    const toRow = Math.max(sel.r1, targetRow);
    const txRange = normalizeRange({ r0: sel.r0, r1: toRow, c0: sel.c0, c1: sel.c1 });

    let res = { rows: new Set(), cols: new Set(), changed: false };
    await runHistoryTransaction(
      {
        label: "autofill",
        ranges: txRange ? [txRange] : [],
        anchor: { r: sel.r0, c: sel.c0 },
      },
      async () => {
        res = fillDownRange(sel, { targetRow });
      },
    );

    if (!res?.changed) return;
    finalizeBulkWrite({
      notifyMsg: "Autofilled.",
      touchedRows: res.rows,
      touchedCols: res.cols,
    });
  }

  // expose
  ctx.copyToClipboard = copyToClipboard;
  ctx.copySelectionToTSV = copySelectionToTSV;
  ctx.copySelectionToHtml = copySelectionToHtml;
  ctx.pasteFromClipboard = pasteFromClipboard;
  ctx.fillDownFromSelection = fillDownFromSelection;
  ctx.fillDownSelectionToRow = fillDownSelectionToRow;

  ctx.copyRowsToTSV = copyRowsToTSV;
  ctx.pasteIntoRows = pasteIntoRows;
  ctx.firstPasteableInputColIndex = firstPasteableInputColIndex;
}
